"use client";

import { useEffect, useState } from "react";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { persist, createJSONStorage } from "zustand/middleware";
import type { Id } from "../../convex/_generated/dataModel";
import {
  calcSubtotal,
  calcTax,
  calcOrderTotal,
  clampDiscountToSubtotal,
} from "../../convex/lib/orderTotals";
import { analytics } from "@/lib/analytics";

// ============================================================================
// Types
// ============================================================================

export interface CartItemModifier {
  modifierId: Id<"menu_modifiers">;
  modifierName: string;
  optionName: string;
  priceAdjustment: number; // In cents
}

export interface CartItem {
  id: string; // Unique cart item ID (for distinguishing same item with different modifiers)
  menuItemId: Id<"menu_items">;
  name: string;
  price: number; // Base price in cents
  quantity: number;
  modifiers: CartItemModifier[];
  specialInstructions?: string;
  photoUrl?: string;
}

export interface DeliveryAddress {
  street: string;
  apt?: string;
  city: string;
  state: string;
  zip: string;
  lat?: number;
  lng?: number;
  instructions?: string;
}

export type OrderType = "pickup" | "delivery";
export type DeliveryProvider = "uber_direct";

export interface UberDirectQuoteState {
  quoteId: string;
  requestKey: string;
  fee: number;
  expiresAt: number;
  dropoffEta?: string;
  pickupEta?: string;
  durationMinutes?: number;
}

interface CartState {
  // Cart items
  items: CartItem[];

  // Order configuration
  orderType: OrderType;
  scheduledFor: number | null; // Unix timestamp, null = ASAP
  deliveryAddress: DeliveryAddress | null;
  deliveryZoneId: Id<"delivery_zones"> | null;
  /**
   * Catering cart only: the platform location that will fulfil the order
   * (the pickup location the guest chose, or the tenant's central kitchen
   * for delivery). The catalog, prices and checkout are all scoped to it.
   * Unused by the meal cart.
   */
  cateringLocationId: string | null;
  /**
   * Catering cart only: true only after the guest explicitly picked a pickup
   * location (card tap). False for a kitchen assigned by switching to
   * delivery, for a legacy auto-default already in storage, and after
   * clearCart. Checkout readiness requires it for pickup.
   */
  cateringLocationChosen: boolean;
  deliveryFee: number; // In cents
  deliveryProvider: DeliveryProvider | null;
  uberDirectQuoteId: string | null;
  uberDirectQuoteRequestKey: string | null;
  uberDirectQuoteExpiresAt: number | null;
  uberDirectDropoffEta: string | null;
  uberDirectPickupEta: string | null;
  uberDirectDurationMinutes: number | null;

  // Payment options
  tipPercent: number; // e.g., 20 for 20%; 0 until checkout preselects or the guest chooses
  tipChosen: boolean; // true only after the guest explicitly picks a tip (incl. "No tip")
  customTipAmount: number | null; // If custom tip, amount in cents
  promoCode: string | null;
  promoDiscount: number; // In cents
  // First-class rewards-points redemption flag, decoupled from promoCode
  // (points and a promo code are mutually exclusive, never encoded as a
  // magic promo string).
  redeemPoints: boolean;
  // A friend's referral code, as typed at checkout (or prefilled from a
  // shared link's ?ref= param). Independent of promoCode/redeemPoints: a
  // referral is not a discount, so it does not clear or get cleared by
  // either. Passed straight through to orders.create; the server resolves
  // and validates it, so this store never judges whether the code is real.
  referralCode: string | null;

  // Tax rate (will be fetched from settings)
  taxRate: number; // e.g., 0.08 for 8%
}

interface CartActions {
  // Item management
  addItem: (item: Omit<CartItem, "id">) => void;
  replaceItem: (itemId: string, item: Omit<CartItem, "id">) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  updateModifiers: (itemId: string, modifiers: CartItemModifier[]) => void;
  updateSpecialInstructions: (itemId: string, instructions: string) => void;
  clearCart: () => void;

  // Order configuration
  setOrderType: (type: OrderType) => void;
  setScheduledFor: (timestamp: number | null) => void;
  setDeliveryAddress: (address: DeliveryAddress | null) => void;
  setDeliveryZone: (zoneId: Id<"delivery_zones"> | null, fee: number) => void;
  setCateringLocation: (locationId: string | null, options?: { chosen?: boolean }) => void;
  /** Catering delivery: flat tenant fee, no courier quote. */
  setFlatDeliveryFee: (fee: number) => void;
  setUberDirectQuote: (quote: UberDirectQuoteState) => void;
  clearUberDirectQuote: () => void;

  // Payment options
  setTipPercent: (percent: number) => void;
  setCustomTip: (amount: number | null) => void;
  preselectTipPercent: (percent: number) => void;
  resetUnchosenTip: () => void;
  setPromoCode: (code: string | null, discount: number) => void;
  setRedeemPoints: (redeem: boolean, discount: number) => void;
  setReferralCode: (code: string | null) => void;
  setTaxRate: (rate: number) => void;

  // Computed values (as functions for reactivity)
  getSubtotal: () => number;
  getTax: () => number;
  getDeliveryFee: () => number;
  getTipAmount: () => number;
  getDiscount: () => number;
  getTotal: () => number;
  getItemCount: () => number;
  getItemById: (itemId: string) => CartItem | undefined;
}

type CartStore = CartState & CartActions;

// ============================================================================
// Helper Functions
// ============================================================================

function generateCartItemId(): string {
  return `cart_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// ============================================================================
// Default Values
// ============================================================================

const DEFAULT_STATE: CartState = {
  items: [],
  orderType: "pickup",
  scheduledFor: null,
  deliveryAddress: null,
  deliveryZoneId: null,
  cateringLocationId: null,
  cateringLocationChosen: false,
  deliveryFee: 0,
  deliveryProvider: null,
  uberDirectQuoteId: null,
  uberDirectQuoteRequestKey: null,
  uberDirectQuoteExpiresAt: null,
  uberDirectDropoffEta: null,
  uberDirectPickupEta: null,
  uberDirectDurationMinutes: null,
  // No silent tip: every pre-checkout total (cart button, View order pill,
  // cart sheet) must match menu math. Checkout defaults to no tip until the
  // guest explicitly chooses one in TipSelector.
  tipPercent: 0,
  tipChosen: false,
  customTipAmount: null,
  promoCode: null,
  promoDiscount: 0,
  redeemPoints: false,
  referralCode: null,
  taxRate: 0.08375, // Default 8.375% Clark County NV sales tax
};

// ============================================================================
// Store
// ============================================================================

export type CartStoreHook = ReturnType<typeof createCartStore>;

/**
 * One persisted cart per purpose. The meal cart (`useCartStore`) and the
 * catering cart (`useCateringCartStore`) share this implementation but never
 * share storage, so adding a catering tray cannot land in a same-day meal
 * order and vice versa.
 */
export function createCartStore(storageName: string) {
  return create<CartStore>()(
  persist(
    (set, get) => ({
      // Initial state
      ...DEFAULT_STATE,

      // ============================================
      // Item Management Actions
      // ============================================

      addItem: (itemData) => {
        const newItem: CartItem = {
          ...itemData,
          id: generateCartItemId(),
        };

        // Track analytics
        const itemTotal = (newItem.price + newItem.modifiers.reduce((sum, m) => sum + m.priceAdjustment, 0)) * newItem.quantity;
        analytics.cartItemAdded(newItem.name, itemTotal, newItem.quantity);

        set((state) => {
          // Check if same item with same modifiers exists
          const existingItemIndex = state.items.findIndex(
            (item) =>
              item.menuItemId === newItem.menuItemId &&
              JSON.stringify(item.modifiers) ===
                JSON.stringify(newItem.modifiers) &&
              item.specialInstructions === newItem.specialInstructions
          );

          if (existingItemIndex >= 0) {
            // Increment quantity of existing item
            const updatedItems = [...state.items];
            updatedItems[existingItemIndex] = {
              ...updatedItems[existingItemIndex],
              quantity:
                updatedItems[existingItemIndex].quantity + newItem.quantity,
            };
            return { items: updatedItems };
          }

          // Add as new item
          return { items: [...state.items, newItem] };
        });
      },

      replaceItem: (itemId, itemData) => {
        set((state) => {
          const currentIndex = state.items.findIndex((item) => item.id === itemId);
          if (currentIndex < 0) {
            return state;
          }

          const replacementItem: CartItem = {
            ...itemData,
            id: itemId,
          };

          const mergeIndex = state.items.findIndex(
            (item, index) =>
              index !== currentIndex &&
              item.menuItemId === replacementItem.menuItemId &&
              JSON.stringify(item.modifiers) ===
                JSON.stringify(replacementItem.modifiers) &&
              item.specialInstructions === replacementItem.specialInstructions
          );

          if (mergeIndex >= 0) {
            const mergedItems = state.items.filter((item) => item.id !== itemId);
            const survivingIndex = mergedItems.findIndex(
              (item) => item.id === state.items[mergeIndex].id
            );

            if (survivingIndex >= 0) {
              mergedItems[survivingIndex] = {
                ...mergedItems[survivingIndex],
                quantity: mergedItems[survivingIndex].quantity + state.items[currentIndex].quantity,
                name: replacementItem.name,
                price: replacementItem.price,
                photoUrl: replacementItem.photoUrl,
              };
            }

            return { items: mergedItems };
          }

          return {
            items: state.items.map((item) =>
              item.id === itemId ? replacementItem : item
            ),
          };
        });
      },

      removeItem: (itemId) => {
        const item = get().items.find((i) => i.id === itemId);
        if (item) {
          analytics.cartItemRemoved(item.name);
        }
        set((state) => ({
          items: state.items.filter((item) => item.id !== itemId),
        }));
      },

      updateQuantity: (itemId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(itemId);
          return;
        }

        set((state) => ({
          items: state.items.map((item) =>
            item.id === itemId ? { ...item, quantity } : item
          ),
        }));
      },

      updateModifiers: (itemId, modifiers) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === itemId ? { ...item, modifiers } : item
          ),
        }));
      },

      updateSpecialInstructions: (itemId, instructions) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === itemId
              ? { ...item, specialInstructions: instructions || undefined }
              : item
          ),
        }));
      },

      clearCart: () => {
        set({
          items: [],
          cateringLocationChosen: false,
          scheduledFor: null,
          deliveryFee: 0,
          deliveryProvider: null,
          uberDirectQuoteId: null,
          uberDirectQuoteRequestKey: null,
          uberDirectQuoteExpiresAt: null,
          uberDirectDropoffEta: null,
          uberDirectPickupEta: null,
          uberDirectDurationMinutes: null,
          promoCode: null,
          promoDiscount: 0,
          redeemPoints: false,
          referralCode: null,
          customTipAmount: null,
        });
      },

      // ============================================
      // Order Configuration Actions
      // ============================================

      setOrderType: (type) => {
        set(() => ({
          orderType: type,
          // Clear delivery info when switching to pickup
          ...(type === "pickup" && {
            deliveryAddress: null,
            deliveryZoneId: null,
            deliveryFee: 0,
            deliveryProvider: null,
            uberDirectQuoteId: null,
            uberDirectQuoteRequestKey: null,
            uberDirectQuoteExpiresAt: null,
            uberDirectDropoffEta: null,
            uberDirectPickupEta: null,
            uberDirectDurationMinutes: null,
          }),
          ...(type === "delivery" && {
            scheduledFor: null,
          }),
        }));
      },

      setScheduledFor: (timestamp) => {
        set({ scheduledFor: timestamp });
      },

      setDeliveryAddress: (address) => {
        set((state) => ({
          deliveryAddress: address,
          ...(!address && {
            deliveryProvider: null,
            uberDirectQuoteId: null,
            uberDirectQuoteRequestKey: null,
            uberDirectQuoteExpiresAt: null,
            uberDirectDropoffEta: null,
            uberDirectPickupEta: null,
            uberDirectDurationMinutes: null,
            deliveryFee: 0,
          }),
          ...(address && state.deliveryAddress
            ? {
                deliveryProvider: null,
                uberDirectQuoteId: null,
                uberDirectQuoteRequestKey: null,
                uberDirectQuoteExpiresAt: null,
                uberDirectDropoffEta: null,
                uberDirectPickupEta: null,
                uberDirectDurationMinutes: null,
                deliveryFee: 0,
              }
            : {}),
        }));
      },

      setDeliveryZone: (zoneId, fee) => {
        set({ deliveryZoneId: zoneId, deliveryFee: fee });
      },

      setCateringLocation: (locationId, options) => {
        set((state) => ({
          cateringLocationId: locationId,
          // Only an explicit guest choice confirms a pickup location; a
          // delivery-kitchen assignment or a null reset never does.
          cateringLocationChosen: locationId != null && options?.chosen === true,
          // Catalog and prices are location-scoped: a location change empties
          // the cart rather than carrying another location's items across.
          ...(state.cateringLocationId && state.cateringLocationId !== locationId
            ? { items: [] }
            : {}),
        }));
      },

      setFlatDeliveryFee: (fee) => {
        set({
          deliveryProvider: null,
          uberDirectQuoteId: null,
          uberDirectQuoteRequestKey: null,
          uberDirectQuoteExpiresAt: null,
          uberDirectDropoffEta: null,
          uberDirectPickupEta: null,
          uberDirectDurationMinutes: null,
          deliveryFee: fee,
        });
      },

      setUberDirectQuote: (quote) => {
        set({
          deliveryProvider: "uber_direct",
          uberDirectQuoteId: quote.quoteId,
          uberDirectQuoteRequestKey: quote.requestKey,
          uberDirectQuoteExpiresAt: quote.expiresAt,
          uberDirectDropoffEta: quote.dropoffEta ?? null,
          uberDirectPickupEta: quote.pickupEta ?? null,
          uberDirectDurationMinutes: quote.durationMinutes ?? null,
          deliveryFee: quote.fee,
        });
      },

      clearUberDirectQuote: () => {
        set({
          deliveryProvider: null,
          uberDirectQuoteId: null,
          uberDirectQuoteRequestKey: null,
          uberDirectQuoteExpiresAt: null,
          uberDirectDropoffEta: null,
          uberDirectPickupEta: null,
          uberDirectDurationMinutes: null,
          deliveryFee: 0,
        });
      },

      // ============================================
      // Payment Options Actions
      // ============================================

      setTipPercent: (percent) => {
        const subtotal = get().getSubtotal();
        const tipAmount = Math.round((subtotal * percent) / 100);
        analytics.tipSelected(percent, tipAmount);
        set({ tipPercent: percent, customTipAmount: null, tipChosen: true });
      },

      setCustomTip: (amount) => {
        if (amount !== null) {
          analytics.tipSelected(0, amount);
        }
        set({ customTipAmount: amount, tipPercent: 0, tipChosen: true });
      },

      // Checkout-mount preselect: shows the familiar 20% chip and keeps the
      // CTA total and createOrder payload consistent with it, but is NOT a
      // guest decision, so tipChosen stays false and no analytics fire.
      preselectTipPercent: (percent) => {
        set({ tipPercent: percent, customTipAmount: null });
      },

      // Leaving checkout without ever touching the tip selector drops the
      // preselect so pre-checkout totals go back to tip-free.
      resetUnchosenTip: () => {
        if (get().tipChosen) return;
        set({ tipPercent: 0, customTipAmount: null });
      },

      setPromoCode: (code, discount) => {
        // A promo code and points redemption are mutually exclusive; applying
        // one clears the other rather than letting both linger in state.
        set({ promoCode: code, promoDiscount: discount, redeemPoints: false });
      },

      setRedeemPoints: (redeem, discount) => {
        set({
          redeemPoints: redeem,
          promoDiscount: redeem ? discount : 0,
          promoCode: redeem ? null : get().promoCode,
        });
      },

      // Untrusted input either way (typed by the guest or lifted from a
      // ?ref= URL param), so this store just holds the string as-is. The
      // server is the only place that resolves it to a member, tenant-scoped,
      // and silently ignores anything that doesn't match.
      setReferralCode: (code) => {
        set({ referralCode: code });
      },

      setTaxRate: (rate) => {
        set({ taxRate: rate });
      },

      // ============================================
      // Computed Values
      // ============================================

      getSubtotal: () => {
        const state = get();
        return calcSubtotal(
          state.items.map((item) => ({
            unitPrice: item.price,
            modifierTotal: item.modifiers.reduce(
              (sum, mod) => sum + mod.priceAdjustment,
              0
            ),
            quantity: item.quantity,
          }))
        );
      },

      getTax: () => {
        const state = get();
        const subtotal = get().getSubtotal();
        const discount = get().getDiscount();
        return calcTax(subtotal, discount, state.taxRate);
      },

      getDeliveryFee: () => {
        const state = get();
        if (state.orderType !== "delivery") return 0;
        if (
          state.deliveryProvider === "uber_direct" &&
          (!state.uberDirectQuoteExpiresAt || state.uberDirectQuoteExpiresAt <= Date.now())
        ) {
          return 0;
        }
        return state.deliveryFee;
      },

      getTipAmount: () => {
        const state = get();
        if (state.customTipAmount !== null) {
          return state.customTipAmount;
        }
        const subtotal = get().getSubtotal();
        return Math.round((subtotal * state.tipPercent) / 100);
      },

      getDiscount: () => {
        const state = get();
        const subtotal = get().getSubtotal();
        return clampDiscountToSubtotal(state.promoDiscount, subtotal);
      },

      getTotal: () => {
        return calcOrderTotal({
          subtotal: get().getSubtotal(),
          appliedDiscount: get().getDiscount(),
          tax: get().getTax(),
          deliveryFee: get().getDeliveryFee(),
          tip: get().getTipAmount(),
        });
      },

      getItemCount: () => {
        const state = get();
        return state.items.reduce((count, item) => count + item.quantity, 0);
      },

      getItemById: (itemId) => {
        const state = get();
        return state.items.find((item) => item.id === itemId);
      },
    }),
    {
      name: storageName,
      storage: createJSONStorage(() => localStorage),
      version: 1,
      // v0 shipped tipPercent: 20 as a silent default, so every persisted cart
      // carried a tip the guest never picked. Exactly 20 with no custom amount
      // is treated as that old default and reset; any other value (18, 25, 0,
      // custom) was an explicit checkout tap, so it's kept and marked chosen
      // so the checkout preselect never overrides it.
      migrate: (persistedState, version) => {
        const state = (persistedState ?? {}) as Record<string, unknown>;
        if (version < 1) {
          const tipPercent =
            typeof state.tipPercent === "number" ? state.tipPercent : 20;
          const customTipAmount =
            typeof state.customTipAmount === "number"
              ? state.customTipAmount
              : null;
          if (tipPercent === 20 && customTipAmount === null) {
            state.tipPercent = 0;
            state.tipChosen = false;
          } else {
            state.tipChosen = true;
          }
        }
        return state as unknown as CartStore;
      },
      // Don't persist everything - exclude tax rate (should be fetched fresh)
      partialize: (state) => ({
        items: state.items,
        orderType: state.orderType,
        scheduledFor: state.scheduledFor,
        deliveryAddress: state.deliveryAddress,
        deliveryZoneId: state.deliveryZoneId,
        cateringLocationId: state.cateringLocationId,
        cateringLocationChosen: state.cateringLocationChosen,
        deliveryFee: state.deliveryFee,
        deliveryProvider: state.deliveryProvider,
        uberDirectQuoteId: state.uberDirectQuoteId,
        uberDirectQuoteRequestKey: state.uberDirectQuoteRequestKey,
        uberDirectQuoteExpiresAt: state.uberDirectQuoteExpiresAt,
        uberDirectDropoffEta: state.uberDirectDropoffEta,
        uberDirectPickupEta: state.uberDirectPickupEta,
        uberDirectDurationMinutes: state.uberDirectDurationMinutes,
        // Only a tip the guest explicitly chose is persisted. The checkout
        // preselect stays memory-only, so a tab closed mid-checkout can't
        // resurrect a tip the guest never picked.
        tipPercent: state.tipChosen ? state.tipPercent : 0,
        tipChosen: state.tipChosen,
        customTipAmount: state.tipChosen ? state.customTipAmount : null,
        promoCode: state.promoCode,
        promoDiscount: state.promoDiscount,
        redeemPoints: state.redeemPoints,
        referralCode: state.referralCode,
        // taxRate is NOT persisted - fetched from server
      }),
    }
  )
  );
}

/** The regular meal cart. Storage key predates multi-tenancy; keep it stable. */
export const useCartStore = createCartStore("tuscan-cove-cart");

/** The catering cart: separate storage, same shape. */
export const useCateringCartStore = createCartStore("restaurant-catering-cart");

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook to get cart totals in a formatted object
 */
export function useCartTotals(store: CartStoreHook = useCartStore) {
  // Shallow-compare a derived snapshot so consumers (CartSheet, OrderSummary,
  // checkout) only re-render when a total actually changes, not on every
  // unrelated store mutation (e.g. Uber-quote fields or a tip keystroke that
  // doesn't move the displayed amounts).
  return store(
    useShallow((store) => ({
      subtotal: store.getSubtotal(),
      tax: store.getTax(),
      deliveryFee: store.getDeliveryFee(),
      tip: store.getTipAmount(),
      discount: store.getDiscount(),
      total: store.getTotal(),
      itemCount: store.getItemCount(),
    })),
  );
}

/**
 * Format cents to dollar string
 */
export function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Check if cart is empty
 */
export function useIsCartEmpty(store: CartStoreHook = useCartStore): boolean {
  return store((state) => state.items.length === 0);
}

type CartPersistApi = {
  hasHydrated: () => boolean;
  onFinishHydration: (fn: () => void) => () => void;
};

/**
 * Zustand persist is missing on the Next.js server bundle for these stores
 * (`store.persist` is undefined). Reading it during render / useState init
 * 500s `/order/catering/checkout`. Treat a missing persist API as not
 * hydrated until the client effect runs.
 */
export function readCartPersistHydrated(store: {
  persist?: CartPersistApi;
}): boolean {
  return store.persist?.hasHydrated() === true;
}

/**
 * Whether the persisted cart has finished rehydrating from localStorage.
 *
 * Guards against the empty-cart redirect race: on a hard load / deeplink /
 * refresh of a cart-dependent page, the component mounts (and any "redirect if
 * empty" effect runs) before zustand persist has rehydrated `items`. Gate that
 * redirect on this hook so a guest with a full cart isn't bounced off checkout.
 */
export function useCartHasHydrated(store: CartStoreHook = useCartStore): boolean {
  // Always start false so SSR never touches `store.persist` (undefined in the
  // server chunk) and so the first client paint matches the server spinner.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const persist = store.persist;
    if (!persist) {
      setHydrated(true);
      return;
    }
    const unsubFinish = persist.onFinishHydration(() => setHydrated(true));
    // Covers the case where hydration already completed before this effect ran.
    setHydrated(readCartPersistHydrated(store));
    return () => {
      unsubFinish();
    };
  }, [store]);

  return hydrated;
}
